import { npcs } from '../data/guide/npcs'
import { entityImages } from '../data/guide/entityImages'
import { npcSheetIds } from '../data/guide/npcPhotoSheets'
import { npcIdentities } from '../data/guide/npcIdentity'
import { invasionPolicies } from '../data/world/invasionPolicies'
import { goldenMonsters } from '../data/guide/goldenInvasion'
import type { Monster, WorldData } from '../types/world'

/**
 * 06_monsters.csv (como el Monster.txt del servidor) mezcla monstruos, NPC, objetos y elementos de eventos.
 * Prioridad de clasificación (la primera que aplique gana; una regla automática nunca sobrescribe una confirmación):
 *  1. Confirmación de administración: NPC de la guía, identidades NPC del staff, fotos del staff con su tipo, objetos del staff.
 *  2. Función explícita en el servidor o en la guía documentada: NPC que entrega misiones; objetivo de caza de misión;
 *     enemigo de invasión/evento acreditado (políticas de invasión, Invasión de Dorados). Se conservan aunque no tengan
 *     spot permanente ni drop.
 *  3. Rótulo NPC en las láminas VANGUARD_NPC: asocia la foto y el tipo; NO acredita su función.
 *  4. Evidencia combinada: al menos dos fuentes independientes (spot de caza, rutas de drop, ubicación ordinaria).
 *     Una sola ubicación, spawn o recompensa al destruirla no demuestra que sea un monstruo.
 *  5. Resto: "Entidad pendiente de clasificación".
 * Las contradicciones entre fuentes se marcan para revisión. La exportación del 08/10/2026 no trae un campo de tipo de
 * entidad, así que ningún objeto se clasifica automáticamente. Nunca se clasifica por nombre, ID, nivel ni foto.
 */
export type MonsterKind = 'monstruo' | 'npc'
export type EntityClassification = 'npc' | 'monstruo' | 'objeto' | 'pendiente'

export const classificationLabel: Record<EntityClassification, string> = {
  npc: 'NPC',
  monstruo: 'Monstruo',
  objeto: 'Objeto',
  pendiente: 'Entidad pendiente de clasificación',
}

/** Objetos con clasificación documentada por el staff o por un tipo explícito del servidor. */
export const documentedObjectIds = new Set<number>()

const cache = new WeakMap<WorldData, Set<number>>()

function npcIds(w: WorldData): Set<number> {
  const hit = cache.get(w)
  if (hit) return hit
  const ids = new Set<number>()
  npcs.forEach((n) => n.monsterId !== undefined && ids.add(n.monsterId))
  npcSheetIds.forEach((id) => ids.add(id))
  entityImages.forEach((e) => e.kind === 'npc' && e.monsterId !== undefined && ids.add(e.monsterId))
  w.quests?.rules.forEach((r) => r.npcMonsterId !== undefined && ids.add(r.npcMonsterId))
  npcIdentities.forEach((n) => ids.add(n.id))
  cache.set(w, ids)
  return ids
}

export function monsterKind(w: WorldData, m: Monster): MonsterKind {
  return npcIds(w).has(m.id) ? 'npc' : 'monstruo'
}

export type ClassificationLevel = 'administración' | 'función explícita' | 'lámina NPC' | 'evidencia combinada' | 'sin evidencia suficiente'

export type Classification = { cls: EntityClassification; level: ClassificationLevel; evidence: string; conflict?: string }

const adminNpc = new Set<number>([
  ...npcs.map((n) => n.monsterId).filter((v): v is number => v !== undefined),
  ...npcIdentities.map((n) => n.id),
  ...entityImages.filter((e) => e.kind === 'npc' && e.monsterId !== undefined).map((e) => e.monsterId as number),
])
const adminMonster = new Set<number>(entityImages.filter((e) => e.kind === 'monstruo' && e.monsterId !== undefined).map((e) => e.monsterId as number))
const documentedEventEnemies = new Set<number>([...invasionPolicies.flatMap((p) => p.monsterIds), ...goldenMonsters.map((g) => g.id)])

const clsCache = new WeakMap<WorldData, Map<number, Classification>>()

export function classifyEntity(w: WorldData, m: Monster): Classification {
  let map = clsCache.get(w)
  if (!map) {
    map = new Map()
    clsCache.set(w, map)
  }
  const hit = map.get(m.id)
  if (hit) return hit

  const id = m.id
  const questGiver = !!w.quests?.rules.some((r) => r.npcMonsterId === id)
  const questTarget = !!w.quests?.objectives.some((o) => o.monsterId === id)
  const eventEnemy = documentedEventEnemies.has(id)
  const spawns = w.idx.spawnsByMonster.get(id) ?? []
  const hunt: string[] = []
  if (spawns.some((s) => s.kind === 'spot')) hunt.push('spot en 08_spawns')
  if ((w.idx.routeTargetsByMonster.get(id)?.length ?? 0) > 0 || (w.idx.targetsByMonster.get(id)?.length ?? 0) > 0) hunt.push('rutas de drop')
  if (w.idx.locationsByMonster.get(id)?.some((l) => l.kind === 'ordinary')) hunt.push('ubicación ordinaria en 09')
  const invasionSpawn = spawns.some((s) => s.kind === 'invasion')

  const enemySignals = [questTarget && 'objetivo de caza de misión', eventEnemy && 'enemigo de invasión documentado'].filter(Boolean) as string[]
  const npcSignals = [questGiver && 'entrega misiones'].filter(Boolean) as string[]

  let out: Classification
  if (adminNpc.has(id)) {
    out = { cls: 'npc', level: 'administración', evidence: 'NPC confirmado por el staff', conflict: enemySignals.length ? `También figura como ${enemySignals.join(' y ')}` : undefined }
  } else if (adminMonster.has(id)) {
    out = { cls: 'monstruo', level: 'administración', evidence: 'Monstruo confirmado por el staff (foto aprobada)', conflict: npcSignals.length ? `También ${npcSignals.join(' y ')}` : undefined }
  } else if (documentedObjectIds.has(id)) {
    out = { cls: 'objeto', level: 'administración', evidence: 'Objeto según el staff' }
  } else if (questGiver) {
    out = { cls: 'npc', level: 'función explícita', evidence: 'Entrega misiones (36/37_quests)', conflict: enemySignals.length ? `También figura como ${enemySignals.join(' y ')}` : undefined }
  } else if (enemySignals.length) {
    out = {
      cls: 'monstruo',
      level: 'función explícita',
      evidence: enemySignals.join(' · '),
      conflict: npcSheetIds.has(id) ? 'Rotulado como NPC en las láminas VANGUARD_NPC' : undefined,
    }
  } else if (npcSheetIds.has(id)) {
    out = { cls: 'npc', level: 'lámina NPC', evidence: 'Rotulado NPC en las láminas VANGUARD_NPC; función no acreditada', conflict: hunt.length >= 2 ? `También tiene ${hunt.join(' + ')}` : undefined }
  } else if (hunt.length >= 2) {
    out = { cls: 'monstruo', level: 'evidencia combinada', evidence: hunt.join(' + ') }
  } else {
    const single = [...hunt, invasionSpawn && 'aparición de invasión sin función documentada'].filter(Boolean) as string[]
    out = {
      cls: 'pendiente',
      level: 'sin evidencia suficiente',
      evidence: single.length
        ? `Solo ${single.join(' + ')}: no basta para clasificarla (puede ser puerta, estatua, trampa, cofre o elemento de evento)`
        : (w.idx.locationsByMonster.get(id)?.length ?? 0) > 0
          ? 'Solo figura en eventos, sin función documentada'
          : 'Sin apariciones, drops ni funciones en los archivos',
    }
  }
  map.set(id, out)
  return out
}

/** Contradicciones entre fuentes de clasificación, para revisión del staff. */
export function classificationConflicts(w: WorldData): { id: number; name: string; cls: EntityClassification; conflict: string }[] {
  return Array.from(w.monsters.values())
    .map((m) => ({ m, c: classifyEntity(w, m) }))
    .filter((x) => x.c.conflict)
    .map((x) => ({ id: x.m.id, name: x.m.name, cls: x.c.cls, conflict: x.c.conflict! }))
}
